import React, { useState } from 'react';
import './context-menu.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons

// classes
import { AbbreviatedText } from '../../utils/functions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  CONTEXT_SYSTEMS,
  NUM_CONTEXT_MENUS,
  CONTEXT_MENU_WIDTH,
  ctx_setContextRow,
  ctx_setContextVisible,
} from './context-menu-slice';
import {
  ctx_calculatePosition,
  ctx_onContextMenuClick,
  ctx_openSubMenu,
} from './context-menu-actions';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

export interface ContextMenuItem {
  /*
   **	the items property is an array of context-menu rows:
   **
   **	text: the row text
   **	childMenu: the index of the sub menu from this row. -1 if there is no sub menu
   **	onClick: true if something needs to happen when the user clicks on this row (props.onClick is called)
   **	enabled: true if the row is enabled
   */
  id: string;
  text: string;
  shortcut?: string;
  childMenu: number;
  onClick?: boolean;
  enabled?: boolean;
} // ContextMenuItem

export interface ContextMenuType {
  /*
   **	contains a list of items, and the index of the parent menu.
   */
  items: ContextMenuItem[];
  parentMenu: number;
  parentRowID: number;
} // ContextMenuType

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export function ContextMenu(props: { system: CONTEXT_SYSTEMS }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  // context menus.
  const sContextVisible: boolean[][] = useAppSelector(
    (state) => state.contextMenu.contextVisible,
  );
  const sContextPos: {
    x: number;
    y: number;
  }[][] = useAppSelector((state) => state.contextMenu.contextPos);
  const sContextMenuDefinition: ContextMenuType[][] = useAppSelector(
    (state) => state.contextMenu.contextMenuDefinition,
  );

  //  ------------------------------------------------------------
  //
  //	Component code
  //
  //  ------------------------------------------------------------

  return (
    <>
      {sContextMenuDefinition[props.system].map((menu, mnuIndex) => (
        <div
          style={{
            display: sContextVisible[props.system][mnuIndex] === true ? 'flex' : 'none',
            flexDirection: 'row',
            position: 'absolute',
            left: sContextPos[props.system][mnuIndex].x.toString() + 'px',
            top: sContextPos[props.system][mnuIndex].y.toString() + 'px',
            zIndex: '2',
          }}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
          }}
        >
          {/* the context menu. the border, background colour, width and height are set here */}
          <div
            className="context-menu-container"
            style={{
              flex: '0 0 ' + CONTEXT_MENU_WIDTH[props.system].toString() + 'px',
              height:
                (
                  20 +
                  sContextMenuDefinition[props.system][mnuIndex].items.length * 30
                ).toString() + 'px',
              display: sContextVisible[props.system][mnuIndex] === true ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'left',
              margin: '0px 0px 0px 0px',
            }}
          >
            {/* a small gap */}
            <div style={{ flex: '0 0 10px' }} />

            {/* followed by all the rows for the context menu */}
            {sContextMenuDefinition[props.system][mnuIndex].items.map((item, index) => (
              <ContextMenuRow
                system={props.system}
                item={item}
                index={index}
                menuID={mnuIndex}
              />
            ))}

            {/* finally another small gap at the bottom */}
            <div style={{ flex: '0 0 10px' }} />
          </div>
        </div>
      ))}
    </>
  );
} // ContextMenu

//  ------------------------------------------------------------
//
//  onMouseEnter and onClick are set here.
//  we have one row for each item in the context-menu definition.
//  if the childMenu property is not -1 then the row will include
//  an arrow linking it the child menu.
//
//  ------------------------------------------------------------

function ContextMenuRow(props: {
  system: CONTEXT_SYSTEMS;
  item: ContextMenuItem;
  index: number;
  menuID: number;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // context menus.
  const sContextRow: number[][] = useAppSelector((state) => state.contextMenu.contextRow);
  const sContextMenuDefinition: ContextMenuType[][] = useAppSelector(
    (state) => state.contextMenu.contextMenuDefinition,
  );
  const sContextVisible: boolean[][] = useAppSelector(
    (state) => state.contextMenu.contextVisible,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  Handler for clicks on menu items.
  //
  //  ------------------------------------------------------------

  function onClick(args: { item: ContextMenuItem; itemID: number }) {
    //
    // check if we've got a handler defined.
    if (args.item.onClick === true) {
      //
      // call the handler.
      ctx_onContextMenuClick({
        contextSystem: props.system,
        contextMenuID: props.menuID,
        rowID: sContextRow[props.system],
      });

      // remove the context menu.
      dispatch(
        ctx_setContextVisible({
          contextSystem: props.system,
          visible: false,
        }),
      );
      dispatch(
        ctx_setContextRow({
          contextSystem: props.system,
          row: -1,
        }),
      );
      //
    }
    //
  } // onClick

  //  ------------------------------------------------------------
  //
  //  Handler for when the pointer enters a row on the context
  //  menu.
  //
  //  ------------------------------------------------------------

  function onMouseEnter(args: { item: ContextMenuItem; itemID: number }) {
    //
    // ensure we have a menu ID and an item ID.
    if (props.menuID > -1 && args.itemID > -1) {
      //
      // set the row on the parent menu, which will have been cleared by onMouseLeave.
      if (sContextMenuDefinition[props.system][props.menuID].parentMenu !== -1) {
        //
        dispatch(
          ctx_setContextRow({
            contextSystem: props.system,
            contextMenu: sContextMenuDefinition[props.system][props.menuID].parentMenu,
            row: sContextMenuDefinition[props.system][props.menuID].parentRowID,
          }),
        );
        //
      }

      if (sContextRow[props.system][props.menuID] !== args.itemID) {
        //
        // check if this context-menu item is enabled.
        if (args.item.enabled === true) {
          //
          // set the focus on this row.
          dispatch(
            ctx_setContextRow({
              contextSystem: props.system,
              contextMenu: props.menuID,
              row: args.itemID,
            }),
          );

          // close any open sub-menus.
          dispatch(
            ctx_setContextVisible({
              contextSystem: props.system,
              contextMenu: props.menuID,
              visible: true,
            }),
          );
          //
        }

        // show a sub menu if one exists.
        ctx_openSubMenu({
          contextSystem: props.system,
          parentMenu: props.menuID,
          parentRowID: args.itemID,
        });
        //
      }
      //
    }
    //
  } // onMouseEnter

  //  ------------------------------------------------------------
  //
  //  Handler for when the pointer leaves a row on the context
  //  menu.
  //
  //  ------------------------------------------------------------

  function onMouseLeave(args: { item: ContextMenuItem; itemID: number }) {
    //
    // ensure we have a menu ID and an item ID.
    if (props.menuID > -1 && args.itemID > -1)
      if (sContextRow[props.system][props.menuID] === args.itemID) {
        //
        // check if this context-menu item is enabled.
        if (args.item.enabled === true) {
          //
          // does this menu have a child menu?
          let childMenuDisplayed: boolean = false;
          if (args.item.childMenu !== -1) {
            //
            // is the child menu currently displayed?
            childMenuDisplayed =
              sContextVisible[props.system][args.item.childMenu] === true;

            // clear the selected row in the child menu.
            dispatch(
              ctx_setContextRow({
                contextSystem: props.system,
                contextMenu: args.item.childMenu,
                row: -1,
              }),
            );
            //
          }

          // clear the selected row in this menu, unless a child menu is open.
          if (childMenuDisplayed === false) {
            dispatch(
              ctx_setContextRow({
                contextSystem: props.system,
                contextMenu: props.menuID,
                row: -1,
              }),
            );
          }
          //
        }
        //
      }
    //
  } // onMouseLeave

  if (props.item.text === '---')
    return (
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div style={{ flex: '0 0 14px' }} />
        <div style={{ flex: '0 0 1px', backgroundColor: '#303030' }} />
        <div style={{ flex: '0 0 1px', backgroundColor: '#202020' }} />
        <div style={{ flex: '0 0 14px' }} />
      </div>
    );
  else
    return (
      <div
        style={{
          flex: '0 0 30px',
          display: 'flex',
          flexDirection: 'row',
          overflow: 'auto',
        }}
        onMouseEnter={(evt) => onMouseEnter({ item: props.item, itemID: props.index })}
        onMouseLeave={(evt) => onMouseLeave({ item: props.item, itemID: props.index })}
        onClick={(evt) => onClick({ item: props.item, itemID: props.index })}
      >
        <div style={{ flex: '0 0 5px' }} />
        <div
          className="context-menu-row"
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'row',
            borderRadius: '5px',
            cursor: sContextMenuDefinition[props.system][props.menuID].items[props.index]
              .onClick
              ? 'pointer'
              : 'default',
          }}
          data-row-selected={
            sContextRow[props.system][props.menuID] === props.index ? 'T' : 'F'
          }
          data-row-enabled={
            sContextMenuDefinition[props.system][props.menuID].items[props.index]
              .enabled === true
              ? 'T'
              : 'F'
          }
        >
          <div style={{ flex: '0 0 10px' }} />
          <div style={{ flex: '1 1', display: 'flex', alignItems: 'center' }}>
            <AbbreviatedText text={props.item.text} />
          </div>
          {props.item.shortcut && (
            <div
              className="context-menu-shortcut"
              style={{
                flex: '0 0 60px',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {props.item.shortcut}
            </div>
          )}
          <div
            style={{
              flex: '0 0 15px',
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              justifyContent: 'center',
              fontSize: '16px',
            }}
          >
            {props.item.childMenu !== -1 ? '▸' : ''}
          </div>
          <div style={{ flex: '0 0 10px' }} />
        </div>
        <div style={{ flex: '0 0 5px' }} />
      </div>
    );
} // ContextMenuRow
